import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Button,
  TablePagination
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import "./Users.css";

const API_URL = process.env.REACT_APP_API_URL;

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // First, fetch the list of users
        const response = await fetch(`${API_URL}/api/github/users`);
        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }
        const usersData = await response.json();
        
        // Then, fetch detailed information for each user to get their names
        const usersWithDetails = await Promise.all(
          usersData.map(async (user) => {
            try {
              const userResponse = await fetch(`${API_URL}/api/github/user/${user.login}`);
              if (userResponse.ok) {
                const userDetails = await userResponse.json();
                return { ...user, name: userDetails.name || user.login };
              }
              return { ...user, name: user.login }; // Fallback to username if details fetch fails
            } catch (error) {
              console.error(`Error fetching details for ${user.login}:`, error);
              return { ...user, name: user.login }; // Fallback to username on error
            }
          })
        );
        
        setUsers(usersWithDetails);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="users-container">
      <button className="users-back-button" onClick={handleBack}>
        &lt; Back
      </button>
      <div className="breadcrumb">
        <a href="/home" className="breadcrumb-link">Dashboard</a>
        <span className="breadcrumb-separator"> &gt; </span>
        <a href="/monitoring-dashboard" className="breadcrumb-link">Monitoring</a>
        <span className="breadcrumb-separator"> &gt; </span>
        <a href="/github-dashboard" className="breadcrumb-link">GitHub Monitoring</a>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-current">Users</span>
      </div>
      <div className="users-header">
        <div className="title-row">
          <span className="users-title">Organization Users</span>
          <span className="users-count"> (Total Users: <span className="count-number">{users.length}</span>)</span>
        </div>
        <div className="users-divider"></div>
        <div className="users-description">
          <p><strong>View all GitHub usernames linked to your organization in one place.</strong></p>
          <p>Quickly track member presence and manage access across monitoring and productivity tools.</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : (
        <TableContainer component={Paper} className="users-table-container">
          <Table className="users-table" aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell className="users-table-header">S.No</TableCell>
                <TableCell className="users-table-header">Employee Name</TableCell>
                <TableCell className="users-table-header">Username</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user, index) => (
                  <TableRow 
                    key={user.id} 
                    className={`users-table-row ${index % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <TableCell className="users-table-cell">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell className="users-table-cell">
                      {user.name || user.login}
                    </TableCell>
                    <TableCell className="users-table-cell">
                      <a 
                        href={user.html_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="user-link"
                      >
                        {user.login}
                      </a>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="pagination-row">
            <div className="pagination-controls">
              <button 
                onClick={() => window.location.href = '/github-dashboard'}
                className="pagination-button"
              >
                Back
              </button>
              <div className="pagination-right">
                <span className="rows-per-page">
                  <span className="rows-label">Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    className="rows-select"
                  >
                    {[5, 10, 25].map(rows => (
                      <option key={rows} value={rows}>
                        {rows}
                      </option>
                    ))}
                  </select>
                </span>
                <span className="page-info">
                  {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, users.length)} of ${users.length}`}
                </span>
                <button 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="pagination-nav"
                >
                  &lt;
                </button>
                <button 
                  onClick={() => setPage(p => (p + 1) * rowsPerPage < users.length ? p + 1 : p)}
                  disabled={(page + 1) * rowsPerPage >= users.length}
                  className="pagination-nav"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </TableContainer>
      )}
    </div>
  );
}

export default Users;
